/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import { defineMessages } from 'react-intl';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import keyBy from 'lodash/keyBy';
import omit from 'lodash/omit';
import values from 'lodash/values';
import moment from 'moment';

import {
  TS_FORMAT,
  DATETIME_LOCAL_MS_FORMAT,
  TIME_GRAINS,
} from '../../../Common/constants/constants';
import { AlertsType, AlertType } from '../../../Common/types/AlertsTypes';
import { DashboardTemplateType } from '../../../Dashboard/types/dashboardTypes';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { calculateLocalImage, filterUserAlertResources, I18NError } from 'Utils';
import { fetchGet, fetchPost, fetchPut, fetchDelete, multipartFormHeaders } from 'FetchAPIs';
import { ApiFilter, UserResourcesType } from 'CommonTypes';
import { stripSystemChunkFromLocationHierarchyPath } from 'hierarchyUtils';

const baseHierarchiesURL = '/api/hierarchies';
const baseHierarchiesAsengineURL = '/api/hierarchies/asengine';
const baseHierarchiesDatalakeURL = '/api/datalake/hierarchies';
const baseHierarchiesImagesURL = '/api/hierarchies/images';

const messages = defineMessages({
  errorGettingHierarchyDashboards: 'Error fetching dashboards. {message}',
  errorGettingHierarchyDashboardById: 'Error fetching dashboard by id. {message}',
  errorCreatingHierarchyDashboard: 'Error creating dashboard. {message}',
  errorUpdatingHierarchyDashboard: 'Error updating dashboard. {message}',
  errorUpdatingHierarchyDashboardStatus: 'Error updating the dashboard status. {message}',
  errorUpdatingHierarchyDashboardSequence: 'Error updating dashboard sequence. {message}',
  errorDeletingHierarchyDashboard: 'Error deleting dashboard. {message}',
  errorGettingHierarchyDashboardImages: 'Error fetching dashboard images. {message}',
  errorGettingHierarchyDashboardImageById: 'Error fetching dashboard image by id. {message}',
  errorUploadingHierarchyDashboardImage: 'Error uploading dashboard image. {message}',
  errorDeletingHierarchyDashboardImage: 'Error deleting dashboard image. {message}',
  errorUpdatingAlerts: 'Error updating alerts',
});

export const getHierarchyDashboardsAPI = async (hierarchyPath: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}${hierarchyPath}/dashboards`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorGettingHierarchyDashboards, error);
    });

export const getHierarchyDashboardById = async (
  hierarchyPath: string,
  dashboardId: string | number
): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}${hierarchyPath}/dashboards/${dashboardId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorGettingHierarchyDashboardById, {
        message: formatErrorResponse(error),
      });
    });

export const createHierarchyDashboard = async (
  hierarchyPath: string,
  dashboardTemplate: DashboardTemplateType,
  userEmailId: string
): Promise<any> =>
  fetchPost(
    `${baseHierarchiesURL}${hierarchyPath}/dashboards`,
    dashboardTemplate,
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorCreatingHierarchyDashboard, {
        message: formatErrorResponse(error),
      });
    });

export const updateHierarchyDashboard = async (
  hierarchyPath: string,
  dashboardId: string | number,
  dashboardTemplate: DashboardTemplateType,
  userEmailId: string
): Promise<any> =>
  fetchPut(
    `${baseHierarchiesURL}${hierarchyPath}/dashboards/${dashboardId}`,
    dashboardTemplate,
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingHierarchyDashboard, {
        message: formatErrorResponse(error),
      });
    });

export const updateHierarchyDashboardStatus = async (
  hierarchyPath: string,
  dashboardId: string | number,
  status: boolean,
  userEmailId: string
): Promise<any> =>
  fetchPut(
    `${baseHierarchiesURL}${hierarchyPath}/dashboards/${dashboardId}/status`,
    null,
    { userEmailId },
    { status }
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingHierarchyDashboardStatus, {
        message: formatErrorResponse(error),
      });
    });

export const updateHierarchyDashboardSequence = async (
  hierarchyPath: string,
  dashboardId: string | number,
  sequenceNumber: number,
  userEmailId: string
): Promise<any> =>
  fetchPut(
    `${baseHierarchiesURL}${hierarchyPath}/dashboards/${dashboardId}/sequence`,
    {},
    { userEmailId },
    { sequenceNumber }
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingHierarchyDashboardSequence, {
        message: formatErrorResponse(error),
      });
    });

export const deleteHierarchyDashboardById = async (
  hierarchyPath: string,
  dashboardId: string | number,
  userEmailId: string
): Promise<any> =>
  fetchDelete(
    `${baseHierarchiesURL}${hierarchyPath}/dashboards/${dashboardId}`,
    null,
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorDeletingHierarchyDashboard, {
        message: formatErrorResponse(error),
      });
    });

// IMAGE MANAGEMENT
export const getHierarchyDashboardImages = async (
  hierarchyPath: string,
  imageIdsFilter: string[] = []
): Promise<any> =>
  fetchGet(`${baseHierarchiesImagesURL}${hierarchyPath}`, null, null)
    .then(response =>
      Promise.all(
        Array.isArray(response?.data)
          ? response.data
              ?.filter(({ imageid }) => isEmpty(imageIdsFilter) || imageIdsFilter.includes(imageid)) // if there is no imageId Filters defined load everything
              .map(image =>
                fetchGet(
                  `${baseHierarchiesImagesURL}${hierarchyPath}/imageId/${image.imageid}`,
                  null,
                  null
                ).then(eachImage =>
                  calculateLocalImage(image.imageid, image.imagefilename, eachImage)
                )
              )
          : []
      )
    )
    .then(images => images)
    .catch(error => {
      throw new I18NError(messages.errorGettingHierarchyDashboardImages, {
        message: formatErrorResponse(error),
      });
    });

export const getHierarchyDashboardImageById = async (
  hierarchyPath: string,
  imageId: string
): Promise<any> =>
  fetchGet(`${baseHierarchiesImagesURL}${hierarchyPath}/imageId/${imageId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorGettingHierarchyDashboardImageById, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Creates a new image
 * @param hierarchyPath path of the hiearchy the image belongs
 * @param image  {imageid: the image symbolic id, file: the image file}
 * @param userEmailId email id of the user uploading image
 * @returns
 */
export const uploadHierarchyDashboardImage = async (
  hierarchyPath: string,
  image: any,
  userEmailId: string
): Promise<any> => {
  const fileForm = new FormData();
  fileForm.set('imageid', image.id);
  fileForm.set('imagefilecontent', image.file);
  return fetchPost(
    `${baseHierarchiesImagesURL}${hierarchyPath}`,
    fileForm,
    { headers: multipartFormHeaders, userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUploadingHierarchyDashboardImage, {
        message: error?.response?.error || formatErrorResponse(error),
      });
    });
};

export const deleteHierarchyDashboardImage = async (
  hierarchyPath: string,
  imageid: string,
  userEmailId: string
): Promise<any> =>
  fetchDelete(`${baseHierarchiesImagesURL}${hierarchyPath}`, null, { userEmailId }, { imageid })
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorDeletingHierarchyDashboardImage, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Returns entities
 * @param entityId (optional): entity id to filter on
 * @param dataFilters (optional) object with dataFilter values to use for filtration
 * @returns entities
 */
export const buildEntities = (
  entityId: string,
  dataFilters: Record<string, Array<string>>
): Record<string, Array<string>> =>
  entityId
    ? { entities: [entityId] } // if entityId exist from filters then use that, overriding dataFilters.deviceid
    : !isEmpty(dataFilters) && 'deviceid' in dataFilters
    ? { entities: dataFilters.deviceid } // if no entityId and dataFilters.deviceid exist
    : {};

/**
 * Returns dimension filters
 * @param dimensionFilter (optional) object with dimensional values to use for filtration, doesn't apply to raw request
 * @param dataFilters (optional) object with dataFilter values to use for filtration
 * @returns dimension_filter
 */
export const buildDimensionFilters = (
  dimensionFilter: Record<string, ApiFilter>,
  dataFilters: Record<string, Array<string>>
): Record<string, Array<string> | unknown> =>
  dimensionFilter
    ? { dimension_filter: dimensionFilter } // if dimensionFilter exist from filters then use that, overriding dataFilters.dimension_filters
    : !isEmpty(dataFilters) && !('deviceid' in dataFilters)
    ? { dimension_filter: omit(dataFilters, 'deviceid') } // if no dimensionFilter and dataFilters.dimension_filters exist
    : {};

/**
 *
 * @param {string} hierarchyPath The path for the url that includes the hierarchy level info
 * @param {object} agg_dict Attributes is an object of attribute names and the functions to apply { input_flow_rate: 'mean', pressure: ['min', 'max'] }
 * @param {object} agg_outputs An object of attribute names and the unique ids they should map to in the response
 * @param {array} raw_attributes An array of attributes to return as raw data
 * @param {object} raw_outputs A map to alias the output fields
 * @param {string} entityId (optional): entity id to filter on
 * @param {object} startRange a moment object that indicates the start range
 * @param {object} endRange a moment object that indicates the end range
 * @param {string} timeGrain (optional) 'day','hour', doesn't apply to raw request
 * @param {array} groupBy (optional) array of column names to group by, doesn't apply to raw request
 * @param {object} dimensionFilter (optional) object with dimensional values to use for filtration, doesn't apply to raw request
 * @param {object} dataFilters (optional) object with dataFilter values to use for filtration
 * @param {string} userEmailId The email address of the current user
 * @param {boolean} isV2DatalakeApiCall Use to determine V2 datalake api call
 * @param {string} cardErrorMessage used for throwing error message on validation
 */
export const getDashboardCardData = (
  hierarchyPath: string,
  agg_dict: Record<string, string | string[]>, // eslint-disable-line @typescript-eslint/naming-convention
  agg_outputs: Record<string, string>, // eslint-disable-line @typescript-eslint/naming-convention
  raw_attributes: Array<string>, // eslint-disable-line @typescript-eslint/naming-convention
  raw_outputs: Record<string, any>, // eslint-disable-line @typescript-eslint/naming-convention
  entityId: string,
  startRange: moment.Moment, // assumes moment
  endRange: moment.Moment, // assumes moment
  timeGrain: string,
  groupBy: Array<string>,
  dimensionFilter: Record<string, ApiFilter>,
  dataFilters: Record<string, Array<string>>,
  rawDataLimit: number,
  userEmailId: string,
  isV2DatalakeApiCall: boolean,
  cardErrorMessage: string
): Promise<any> => {
  const promises = [];

  if (cardErrorMessage) {
    promises.push(Promise.reject(new Error(cardErrorMessage)));
  } else {
    const baseURL = isV2DatalakeApiCall ? baseHierarchiesDatalakeURL : baseHierarchiesAsengineURL;
    // First fetch raw data
    if (!isNil(raw_attributes) && !isEmpty(raw_attributes)) {
      promises.push(
        fetchPost(
          `${baseURL}${hierarchyPath}/queryrawdata`,
          {
            raw_attributes,
            output_map: raw_outputs,
            ...(startRange ? { start_ts: startRange.utc().format(TS_FORMAT) } : {}),
            ...(endRange ? { end_ts: endRange.utc().format(TS_FORMAT) } : {}),
            ...buildEntities(entityId, dataFilters),
            ...buildDimensionFilters(dimensionFilter, dataFilters),
            limit: rawDataLimit,
          },
          { userEmailId },
          null
        )
      );
    }

    if (!isNil(agg_dict) && !isEmpty(agg_dict)) {
      let queryDataAPIPath = 'querydata';
      if (isV2DatalakeApiCall && groupBy) {
        // for V2 datalake API the dynamic aggregation done by queryAggregatedData API. i.e when ever we have groupBy we need to call queryAggregatedData URL path.
        queryDataAPIPath = 'queryAggregatedData';
      }
      // Now fetch aggregated data
      promises.push(
        fetchPost(
          `${baseURL}${hierarchyPath}/${queryDataAPIPath}`,
          {
            agg_dict,
            agg_outputs,
            ...(startRange ? { start_ts: startRange.utc().format(TS_FORMAT) } : {}),
            ...(endRange ? { end_ts: endRange.utc().format(TS_FORMAT) } : {}),
            ...buildEntities(entityId, dataFilters),
            ...buildDimensionFilters(dimensionFilter, dataFilters),
            limit: rawDataLimit,
            ...(!isV2DatalakeApiCall && timeGrain
              ? { time_grain: timeGrain === TIME_GRAINS.HOUR ? '1H' : timeGrain }
              : {}),
            ...(groupBy ? { groupby: groupBy } : {}),
          },
          { userEmailId },
          null
        )
      );
    }
  }

  return Promise.all(promises).then(([rawResponse = [], aggregatedResponse = []]) => {
    // Special case where we merge on timestamp to support charts/tables with both raw and aggregate on the same graph. TODO: do we still need to do this?
    if (
      entityId && // only entityIds have raw metrics
      isNil(groupBy) && // no group by, would have multiple rows
      isNil(dimensionFilter) && // no dimension filter (means no summary dashboard too)
      !isNil(rawResponse) &&
      !isEmpty(rawResponse) &&
      !isNil(aggregatedResponse) &&
      !isEmpty(aggregatedResponse)
    ) {
      // if both the raw and aggregated response has a timestamp column, merge on it
      if (
        aggregatedResponse.some(aggData => aggData.timestamp) &&
        rawResponse.some(rawData => rawData.timestamp)
      ) {
        const merged = merge(
          keyBy(rawResponse, 'timestamp'),
          keyBy(aggregatedResponse, 'timestamp')
        );
        return values(merged);
      }
      if (aggregatedResponse.length === 1 && rawResponse.length === 1) {
        // in this case we have a simple value card with both raw and aggregate metrics and need to just merge the simple raw and aggregate values into the same record
        return [{ ...aggregatedResponse[0], ...rawResponse[0] }];
      }
    }

    // Otherwise just return either or both
    return [
      ...(Array.isArray(rawResponse) ? rawResponse : [rawResponse]),
      ...(Array.isArray(aggregatedResponse) ? aggregatedResponse : [aggregatedResponse]),
    ];
  });
};

// Gets alert count for current hierarchy level and all children
export const getDashboardHierarchyAlertCount = (
  hierarchyPath: string,
  dimensionFilter: Record<string, ApiFilter> | null,
  startDate: number,
  endDate: number,
  groupByField: string,
  search: string | null,
  includeChildren: boolean,
  userEmailId: string
): Promise<number> =>
  fetchPost(
    `${baseHierarchiesDatalakeURL}${hierarchyPath}${
      includeChildren ? '/hierarchy' : ''
    }/alerts/count`,
    {
      ...(search ? { search } : {}),
      ...(dimensionFilter ? { filter: dimensionFilter } : {}),
      tsBegin: moment.utc(startDate).format(DATETIME_LOCAL_MS_FORMAT),
      tsEnd: moment.utc(endDate).format(DATETIME_LOCAL_MS_FORMAT),
      groupByField,
    },
    { userEmailId },
    null
  )
    .then(res => res)
    .catch(error => console.error(error));

/**
 * Gets alerts for current hierarchy level and/or all childrens
 * @param {string} hierarchyPath url of the resource
 * @param {object} dimensionFilter (optional) object with dimensional values to use for filtration, doesn't apply to raw request
 * @param startDateInMs
 * @param endDateInMs
 * @param groupByField
 * @param {string} search search alert by name
 * @param {boolean} includeChildren determine if to get the child resource alert or not
 * @param {string} userEmailId email id of the user
 * @param {number} limit determine the amount of data to return
 * @returns
 */
export const fetchHierarchyAlerts = (
  hierarchyPath: string,
  dimensionFilter: Record<string, ApiFilter | string> | null,
  startDateInMs: number,
  endDateInMs: number,
  groupByField: string,
  search: string,
  includeChildren: boolean,
  userEmailId: string,
  limit = 1000,
  currentUserResources: UserResourcesType
): Promise<AlertsType> => {
  const tsBegin = moment.utc(startDateInMs).format(DATETIME_LOCAL_MS_FORMAT);
  const tsEnd = moment.utc(endDateInMs).format(DATETIME_LOCAL_MS_FORMAT);

  return fetchPost(
    `${baseHierarchiesDatalakeURL}${
      includeChildren ? hierarchyPath : stripSystemChunkFromLocationHierarchyPath(hierarchyPath)
    }${includeChildren ? '/hierarchy' : ''}/alerts/search`,
    {
      ...(search ? { search } : {}),
      ...(dimensionFilter ? { filter: dimensionFilter } : {}),
      tsBegin,
      tsEnd,
      groupByField,
    },
    { userEmailId },
    { limit }
  )
    .then(({ results }) => filterUserAlertResources(results, currentUserResources) as any)
    .catch(error => console.error(error));
};

export const updateAlertsAPI = async (
  alertsToUpdate: Array<AlertType>,
  userEmailId: string
): Promise<any> =>
  fetchPut(
    `${baseHierarchiesDatalakeURL}/workspaces/bulk/alerts`,
    alertsToUpdate,
    { userEmailId },
    null
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorUpdatingAlerts, {
        message: formatErrorResponse(error),
      });
    });
